/**
 * apps/web/src/components/RouteErrorFallback.tsx
 *
 * The UI an ErrorBoundary renders when a subtree crashes. Two shapes:
 *   - chunk-load error → a "new version available, reload" prompt (a stale lazy
 *     chunk after a deploy is the most common cause; an in-place retry can loop,
 *     so we offer a hard reload instead).
 *   - any other error  → an apology + "Riprova" that resets the boundary.
 *
 * Modeled on QueryState's error block so a crashed route looks like a failed
 * query, not a foreign screen. If the thrown error is a typed ApiError we also
 * surface its requestId for support.
 */

import type React from 'react';
import { AlertTriangle, RefreshCw, RotateCcw } from 'lucide-react';
import { Button } from './ui/Button';
import { isApiError } from '../lib/api';
import { useStrings } from '../i18n';

interface RouteErrorFallbackProps {
  readonly error: Error;
  readonly reset: () => void;
  readonly isChunkLoadError: boolean;
  /** `page` fills the viewport (top-level boundary); `inline` sits in content. */
  readonly variant?: 'page' | 'inline';
}

function reload(): void {
  if (typeof window !== 'undefined') window.location.reload();
}

export function RouteErrorFallback({
  error,
  reset,
  isChunkLoadError,
  variant = 'inline',
}: RouteErrorFallbackProps): React.ReactElement {
  const strings = useStrings();
  const requestId = isApiError(error) ? error.requestId : undefined;

  const title = isChunkLoadError ? strings.common.updateAvailableTitle : strings.common.errorTitle;
  const body = isChunkLoadError ? strings.common.updateAvailableBody : strings.common.errorGeneric;

  return (
    <div
      role="alert"
      className={
        variant === 'page'
          ? 'flex min-h-[80vh] flex-col items-center justify-center px-6'
          : 'flex flex-col items-center justify-center rounded-xl border border-dashed border-danger/40 bg-danger-soft/40 px-6 py-12 text-center'
      }
    >
      <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-xl bg-danger-soft text-danger ring-1 ring-danger/20">
        <AlertTriangle className="h-6 w-6" aria-hidden="true" />
      </span>
      <h2 className="font-display text-lg font-semibold text-danger-soft-foreground">{title}</h2>
      <p className="mt-1 max-w-sm text-center text-sm text-muted">{body}</p>

      {isChunkLoadError ? (
        <Button variant="primary" className="mt-5" onClick={reload}>
          <RefreshCw className="h-[18px] w-[18px]" aria-hidden="true" />
          {strings.common.reload}
        </Button>
      ) : (
        <div className="mt-5 flex items-center gap-3">
          <Button variant="secondary" onClick={reset}>
            <RotateCcw className="h-[18px] w-[18px]" aria-hidden="true" />
            {strings.common.retry}
          </Button>
          <Button variant="ghost" onClick={reload}>
            {strings.common.reload}
          </Button>
        </div>
      )}

      {requestId ? (
        <p className="mt-4 font-mono text-[0.7rem] text-muted">ref: {requestId}</p>
      ) : null}
    </div>
  );
}
